import React, { useEffect, useState, useCallback, useRef, useMemo, useContext } from 'react';
import { AgGridTable, Button, Pagination, Drawer } from 'components/common';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import SimpleBar from 'simplebar-react';
import { FaFileSignature, FaPeopleArrows, FaSadTear } from 'react-icons/fa';
import {
  MdNewLabel, MdOutlineRequestQuote, MdList, MdArrowBack, MdTune,
  MdCalendarMonth, MdSearch, MdClose, MdFileDownload, MdAdd,
  MdBarChart, MdAttachMoney, MdFitnessCenter, MdViewKanban,
} from 'react-icons/md';
import { PermissionContext } from 'context/Context';
import {
  getAllProjects, getNewPCVProjects, getQuotationProjects,
  getNegotiationProjects, getJobProjects, getLostProjects,
  setNestedParams, resetNestedParams, setFilterTag, resetFilterTag,
  exportProjects, getPipelineStats,
} from 'redux/crm/projectSlice';
import { jobColumnDefs, pcvColumnDefs } from 'components/app/crm/project/component/Columns';
import FilterAdvanceModal from 'components/app/crm/project/component/FilterAdvanceModal';
import ProjectForm from 'components/app/crm/project/ProjectForm';

// ── Constants & helpers ────────────────────────────────────────────────────────

const SEARCH_ATTR  = 'name_or_q_number_or_j_number_or_company_name_cont';
// Long enough that a pause between digits of a quote number does not fire a
// request, short enough that the list still feels like it is following you.
const SEARCH_DEBOUNCE_MS = 600;
const SEARCH_MIN_LENGTH = 3;
const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtUSD = (v) => {
  if (v == null) return '—';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Number(v).toFixed(0)}`;
};
const fmtMT = (v) => {
  if (v == null) return '—';
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return Number(v).toLocaleString();
};

// sales_pipeline.color (bootstrap palette from DB seed) → badge classes
const PIPELINE_BADGE_CLS = {
  secondary: 'tw-bg-slate-100 tw-text-slate-600',
  primary:   'tw-bg-primary-100 tw-text-primary-700',
  info:      'tw-bg-sky-100 tw-text-sky-700',
  warning:   'tw-bg-amber-100 tw-text-amber-700',
  success:   'tw-bg-emerald-100 tw-text-emerald-700',
  danger:    'tw-bg-red-100 tw-text-red-700',
};
const pipelineCls = (color) => PIPELINE_BADGE_CLS[color] || 'tw-bg-slate-100 tw-text-slate-600';

const statusCls = (id) => {
  if (id === 1 || id === 3)                           return 'tw-bg-slate-100 tw-text-slate-600';
  if (id === 4 || id === 7 || id === 10 || id === 12) return 'tw-bg-amber-100 tw-text-amber-700';
  if (id === 14)                                      return 'tw-bg-sky-100 tw-text-sky-700';
  if (id === 15)                                      return 'tw-bg-emerald-100 tw-text-emerald-700';
  if (id === 17 || id === 18)                         return 'tw-bg-red-100 tw-text-red-700';
  return 'tw-bg-primary-100 tw-text-primary-700';
};

// ── Config ─────────────────────────────────────────────────────────────────────

const ALL_CONFIG = {
  key: 'all', label: 'All Projects', icon: MdList,
  stateKey: 'allData', loadApi: getAllProjects,
  columnDefs: jobColumnDefs, entryType: 'all',
  iconBg: 'tw-bg-slate-100', iconColor: 'tw-text-slate-500', accent: '#64748b',
};

const STAGE_TABS = [
  { key: 'new',         label: 'Discovery',   icon: MdNewLabel,         stateKey: 'newPCVData',      loadApi: getNewPCVProjects,      columnDefs: pcvColumnDefs, entryType: 'new_pcv',     iconBg: 'tw-bg-primary-50', iconColor: 'tw-text-primary-600', accent: '#4f46e5', statsKey: 'new_pcv' },
  { key: 'quotation',   label: 'Quotation',   icon: MdOutlineRequestQuote, stateKey: 'quotationData',  loadApi: getQuotationProjects,   columnDefs: pcvColumnDefs, entryType: 'quotation',   iconBg: 'tw-bg-blue-50',   iconColor: 'tw-text-blue-600',   accent: '#3b82f6', statsKey: 'quotation' },
  { key: 'negotiation', label: 'Negotiation', icon: FaPeopleArrows,     stateKey: 'negotiationData', loadApi: getNegotiationProjects, columnDefs: jobColumnDefs, entryType: 'negotiation', iconBg: 'tw-bg-cyan-50',   iconColor: 'tw-text-cyan-600',   accent: '#06b6d4', statsKey: 'negotiation' },
  { key: 'job',         label: 'Job',         icon: FaFileSignature,    stateKey: 'jobData',         loadApi: getJobProjects,         columnDefs: jobColumnDefs, entryType: 'job',         iconBg: 'tw-bg-emerald-50',iconColor: 'tw-text-emerald-600',accent: '#10b981', statsKey: 'job' },
  { key: 'lost',        label: 'Lost',        icon: FaSadTear,          stateKey: 'lostData',        loadApi: getLostProjects,        columnDefs: jobColumnDefs, entryType: 'lost',        iconBg: 'tw-bg-red-50',    iconColor: 'tw-text-red-500',    accent: '#ef4444', statsKey: 'lost' },
];

const ALL_TABS = [ALL_CONFIG, ...STAGE_TABS];

// The summary cards read "All active stages", so Lost stays out of their totals —
// it keeps its own tab and its own count.
const ACTIVE_STAGE_TABS = STAGE_TABS.filter((t) => t.key !== 'lost');

// ── Summary card ───────────────────────────────────────────────────────────────

const SummaryCard = ({ icon: Icon, label, value, sub, accent = '#64748b', gradient = false }) => {
  const cardStyle = gradient ? {} : {
    background: `linear-gradient(135deg, ${accent}1a 0%, #ffffff 60%)`,
    borderColor: `${accent}30`,
  };
  const iconStyle = gradient ? {} : { background: `${accent}18`, borderRadius: 8 };
  return (
    <div className={`tw-flex-1 tw-min-w-[140px] tw-rounded-lg tw-p-4 tw-shadow-sm tw-border ${
      gradient ? 'tw-bg-gradient-to-br tw-from-primary-500 tw-to-primary-700 tw-border-transparent' : 'tw-border-slate-100'
    }`} style={cardStyle}>
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
        <div className="tw-min-w-0">
          <p className={`tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-mb-1.5 ${gradient ? 'tw-text-primary-200' : 'tw-text-slate-500'}`}>{label}</p>
          <p className={`tw-text-2xl tw-font-bold tw-leading-none tw-tabular-nums tw-truncate ${gradient ? 'tw-text-white' : 'tw-text-slate-800'}`}>{value ?? '—'}</p>
        </div>
        <div className={`tw-p-2 tw-shrink-0 ${gradient ? 'tw-rounded tw-bg-white/20' : ''}`} style={iconStyle}>
          <Icon size={16} style={{ color: gradient ? '#ffffff' : accent }} />
        </div>
      </div>
      {sub && <p className={`tw-text-[11px] tw-font-medium tw-mt-3 tw-truncate ${gradient ? 'tw-text-primary-200' : 'tw-text-slate-400'}`}>{sub}</p>}
    </div>
  );
};
SummaryCard.propTypes = {
  icon: PropTypes.elementType.isRequired, label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sub: PropTypes.string, accent: PropTypes.string, gradient: PropTypes.bool,
};

// ── Pipeline stage card ────────────────────────────────────────────────────────

const PipelineStageCard = ({ tab, count, amount, weight, breakdown, isActive, onClick }) => {
  const Icon = tab.icon;
  const cardStyle = {
    background: `linear-gradient(145deg, ${tab.accent}12 0%, #ffffff 65%)`,
    borderColor: isActive ? tab.accent : `${tab.accent}28`,
    borderWidth: isActive ? 2 : 1,
  };
  return (
    <button type="button" onClick={onClick}
      className={`tw-flex tw-flex-col tw-flex-1 tw-min-w-[120px] tw-text-left tw-rounded-lg tw-border tw-p-3.5 tw-transition-all tw-cursor-pointer ${
        isActive ? 'tw-shadow-md' : 'tw-shadow-sm hover:tw-shadow-md'
      }`}
      style={cardStyle}>
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-2.5">
        <div className="tw-rounded-md tw-p-1" style={{ background: `${tab.accent}20` }}>
          <Icon size={11} style={{ color: tab.accent }} />
        </div>
        <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide" style={{ color: tab.accent }}>{tab.label}</span>
      </div>
      <p className="tw-text-xl tw-font-bold tw-leading-none tw-tabular-nums tw-mb-2" style={{ color: tab.accent }}>
        {count != null ? count.toLocaleString() : '—'}
      </p>
      <div className="tw-flex tw-flex-col tw-gap-0.5">
        <span className="tw-text-[11px] tw-font-semibold tw-tabular-nums" style={{ color: tab.accent }}>{fmtUSD(amount)}</span>
        <span className="tw-text-[11px] tw-text-slate-400 tw-tabular-nums">{fmtMT(weight)} MT</span>
      </div>
      {(breakdown || []).length > 0 && (
        <div className="tw-mt-2 tw-pt-1.5 tw-border-t tw-border-slate-100 tw-flex tw-flex-col tw-gap-0.5">
          {breakdown.map(b => (
            <div key={b.key} className="tw-flex tw-items-center tw-justify-between tw-gap-2">
              <span className="tw-text-[10px] tw-text-slate-400 tw-truncate">{b.label}</span>
              <span className="tw-text-[10px] tw-font-bold tw-tabular-nums" style={{ color: tab.accent }}>{(b.count ?? 0).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
      {/* Accent underline pinned to the card bottom so all cards align */}
      <div className="tw-mt-auto tw-pt-2.5">
        <div className="tw-h-0.5 tw-rounded-xl" style={{ backgroundColor: tab.accent, opacity: isActive ? 1 : 0.3 }} />
      </div>
    </button>
  );
};
PipelineStageCard.propTypes = {
  tab: PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, icon: PropTypes.elementType, iconBg: PropTypes.string, iconColor: PropTypes.string, accent: PropTypes.string }).isRequired,
  count: PropTypes.number, amount: PropTypes.number, weight: PropTypes.number,
  breakdown: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, count: PropTypes.number })),
  isActive: PropTypes.bool.isRequired, onClick: PropTypes.func.isRequired,
};
PipelineStageCard.defaultProps = { breakdown: null };

// ── Kanban card ────────────────────────────────────────────────────────────────

const KanbanCard = ({ project, accent = '#4f46e5' }) => {
  const displayNumber = project.j_number || project.q_number || project.pcv_number;
  return (
    <Link to={`/crm/projects/${project.id}`} target="_blank"
      className="tw-group tw-block tw-no-underline hover:tw-no-underline tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-3.5 tw-shadow-sm hover:tw-shadow-md hover:-tw-translate-y-0.5 tw-transition-all"
      style={{ borderLeft: `3px solid ${accent}`, paddingLeft: '11px' }}>
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-2">
        <span className="tw-text-[11px] tw-font-mono tw-font-semibold tw-shrink-0" style={{ color: accent }}>{displayNumber}</span>
        {project.sales_pipeline ? (
          <span title={project.sales_pipeline.name}
            className={`tw-inline-block tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-[10px] tw-font-semibold tw-max-w-[140px] tw-truncate ${pipelineCls(project.sales_pipeline.color)}`}>
            {project.sales_pipeline.name}
          </span>
        ) : project.status && (
          <span className={`tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-[10px] tw-font-semibold tw-shrink-0 ${statusCls(project.status?.id)}`}>
            {project.status?.name}
          </span>
        )}
      </div>
      <p className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-line-clamp-2 tw-mb-1.5 tw-leading-snug group-hover:tw-text-slate-950">{project.name}</p>
      {project.customer && (
        <p className="tw-text-[11px] tw-text-slate-500 tw-truncate tw-mb-2">{project.customer?.name}</p>
      )}
      <div className="tw-flex tw-items-center tw-justify-between tw-pt-2.5 tw-border-t tw-border-slate-200">
        <span className="tw-text-[11px] tw-font-semibold" style={{ color: accent }}>{fmtUSD(project.amount)}</span>
        {project.weight > 0 && <span className="tw-text-[11px] tw-text-slate-400">{fmtMT(project.weight)} MT</span>}
      </div>
    </Link>
  );
};
KanbanCard.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pcv_number: PropTypes.string, j_number: PropTypes.string, q_number: PropTypes.string,
    name: PropTypes.string, amount: PropTypes.number, weight: PropTypes.number,
    status: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
    sales_pipeline: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, color: PropTypes.string }),
    customer: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
  accent: PropTypes.string,
};

// ── Kanban column ──────────────────────────────────────────────────────────────

const KanbanColumn = ({ tab, stageData, pipelineStats }) => {
  const Icon = tab.icon;
  const { data = [], totalRows = 0 } = stageData || {};
  const stats = pipelineStats?.[tab.statsKey];
  const visible   = data.slice(0, 15);
  const remaining = Math.max(0, totalRows - visible.length);

  return (
    <div className="tw-flex tw-flex-col tw-min-w-[235px] lg:tw-min-w-[220px] tw-flex-1 tw-rounded-xl tw-overflow-hidden"
      style={{ border: `1px solid ${tab.accent}38`, background: '#ffffff' }}>

      {/* Header */}
      <div className="tw-px-3.5 tw-py-3" style={{ borderTop: `4px solid ${tab.accent}`, background: `${tab.accent}12` }}>
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <div className="tw-rounded-md tw-p-1" style={{ background: `${tab.accent}20` }}>
              <Icon size={12} style={{ color: tab.accent }} />
            </div>
            <span className="tw-text-sm tw-font-bold" style={{ color: tab.accent }}>{tab.label}</span>
          </div>
          <span className="tw-rounded-xl tw-text-[10px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-tabular-nums" style={{ background: `${tab.accent}20`, color: tab.accent }}>
            {totalRows > 999 ? '999+' : totalRows.toLocaleString()}
          </span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-3 tw-pl-1">
          <span className="tw-text-[11px] tw-tabular-nums tw-font-semibold" style={{ color: tab.accent }}>{fmtUSD(stats?.amount)}</span>
          <span className="tw-text-[11px] tw-text-slate-500 tw-tabular-nums">{fmtMT(stats?.weight)} MT</span>
        </div>
      </div>

      {/* Cards */}
      <SimpleBar style={{ maxHeight: 'clamp(360px, max(240px, calc(100dvh - 430px)), 620px)', minHeight: 260 }} className="table-scroll tw-flex-1">
        <div className="tw-p-2.5 tw-flex tw-flex-col tw-gap-2.5">
          {visible.length === 0
            ? <p className="tw-text-[11px] tw-text-slate-400 tw-text-center tw-py-6">No projects</p>
            : visible.map(project => <KanbanCard key={project.id} project={project} accent={tab.accent} />)
          }
        </div>
      </SimpleBar>

      {remaining > 0 && (
        <div className="tw-px-3 tw-py-2.5 tw-border-t tw-text-center" style={{ borderColor: `${tab.accent}28`, background: `${tab.accent}0d` }}>
          <span className="tw-text-xs tw-tabular-nums tw-font-semibold" style={{ color: tab.accent }}>+{remaining.toLocaleString()} more</span>
        </div>
      )}
    </div>
  );
};
KanbanColumn.propTypes = {
  tab: PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, icon: PropTypes.elementType, iconBg: PropTypes.string, iconColor: PropTypes.string, accent: PropTypes.string, statsKey: PropTypes.string }).isRequired,
  stageData: PropTypes.object, pipelineStats: PropTypes.object,
};

/**
 * Fetches one stage, and cancels that fetch if the params change again before
 * it lands. Returns the effect cleanup.
 *
 * Each project page is roughly 90 KB, and typing a quote number fires one per
 * debounce window: "24774" produced overlapping requests for "2", "277" and
 * "2774" that all ran to completion. Safari queues them and the page appears to
 * hang; every browser was exposed to the worse half of the bug, which is that
 * the LAST response to arrive wins — so a slow "2" landing after "24774"
 * replaced the right answer with the wrong one.
 */
const fetchStage = ({ dispatch, config, currentParams }) => {
  const json = JSON.stringify(currentParams);
  if (lastFetchedParams.get(config.stateKey) === json) return;

  lastFetchedParams.set(config.stateKey, json);

  // Superseded here, and ONLY here — deliberately not in the effect's cleanup.
  // Cleanup runs on any re-render whose dependencies changed identity, so
  // aborting from there would let an unrelated render kill a request that was
  // still needed, and the params cache would then suppress the retry.
  inFlightStageRequests.get(config.stateKey)?.abort();

  const request = dispatch(config.loadApi(currentParams));
  inFlightStageRequests.set(config.stateKey, request);

  request.finally(() => {
    if (inFlightStageRequests.get(config.stateKey) === request) {
      inFlightStageRequests.delete(config.stateKey);
    }
  });
};

// Headless loader — fetches one stage when its params changed since the last
// fetch (module-level cache, so tab switches don't refire identical requests)
const StageDataLoader = ({ config, currentParams }) => {
  const dispatch = useDispatch();

  useEffect(() => fetchStage({ dispatch, config, currentParams }), [currentParams, dispatch, config]);

  return null;
};
StageDataLoader.propTypes = {
  config: PropTypes.shape({ loadApi: PropTypes.func.isRequired }).isRequired,
  currentParams: PropTypes.object.isRequired,
};

// ── Kanban view ────────────────────────────────────────────────────────────────

const DEFAULT_KANBAN_PARAMS = { page: 1, per_page: 20 };

// Ransack-only params for pipeline_stats (pagination/sort are list concerns)
const LIST_ONLY_PARAMS = ['page', 'per_page', 'sort'];
const statsParams = (params) =>
  Object.fromEntries(Object.entries(params || {}).filter(([k]) => !LIST_ONLY_PARAMS.includes(k)));

// Last-fetched params per stateKey — module-scoped so switching between the
// All Projects and Kanban tabs does not refire identical requests on every
// remount (the loaders below only fetch when params actually changed).
const lastFetchedParams = new Map();
// stateKey → the dispatch promise still in flight for that stage, so a newer
// set of params can cancel the request it replaces.
const inFlightStageRequests = new Map();

const KanbanView = ({ projectState, pipelineStats }) => (
  <>
    {/* Headless data loaders — one per stage */}
    {STAGE_TABS.map(tab => (
      <StageDataLoader
        key={tab.key}
        config={tab}
        currentParams={projectState[tab.stateKey]?.currentParams || DEFAULT_KANBAN_PARAMS}
      />
    ))}

    {/* Board columns */}
    <div className="kanban-board-scroll tw-flex tw-gap-3 tw-overflow-x-auto tw-pb-2" style={{ minHeight: 360 }}>
      {STAGE_TABS.map(tab => (
        <KanbanColumn key={tab.key} tab={tab} stageData={projectState[tab.stateKey]} pipelineStats={pipelineStats} />
      ))}
    </div>
  </>
);
KanbanView.propTypes = {
  projectState: PropTypes.object.isRequired,
  pipelineStats: PropTypes.object,
};

// ── All-tab grid + pagination ──────────────────────────────────────────────────

const SORT_FIELD_MAP = {
  pcv_number:                        'pcv_number',
  name:                              'name',
  q_number:                          'q_number',
  j_number:                          'j_number',
  amount:                            'amount',
  weight:                            'weight',
  area:                              'area',
  pj_province:                       'pj_province',
  sales_incharge:                    'sales_incharge',
  expected_date_of_signing_contract: 'expected_date_of_signing_contract',
  created_date:                      'created_at',
};

const ProjectTabContent = ({ config, entryData }) => {
  const dispatch      = useDispatch();
  const { data = [], totalRows = 0, currentParams = { page: 1, per_page: 50 } } = entryData || {};
  const columns = useMemo(() => config.columnDefs, [config]);

  useEffect(() => fetchStage({ dispatch, config, currentParams }), [currentParams, dispatch, config]);

  const goToPage = useCallback((page, size) => {
    const per_page = size || currentParams.per_page || 50;
    dispatch(setNestedParams({ key: config.stateKey, params: { ...currentParams, page, per_page } }));
  }, [dispatch, config, currentParams]);

  const handleSortChanged = useCallback((e) => {
    const sortedCols = e.api.getColumnState().filter(c => c.sort);
    if (sortedCols.length === 0) {
      const rest = Object.fromEntries(Object.entries(currentParams).filter(([k]) => k !== 'sort'));
      dispatch(setNestedParams({ key: config.stateKey, params: { ...rest, page: 1 } }));
      return;
    }
    const { colId, sort } = sortedCols[0];
    const attribute = SORT_FIELD_MAP[colId];
    if (!attribute) return;
    dispatch(setNestedParams({ key: config.stateKey, params: { ...currentParams, page: 1, sort: { attribute, direction: sort } } }));
  }, [currentParams, dispatch, config]);

  const pageSize   = currentParams.per_page || 50;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  return (
    <>
      {/* `height`, not `gridHeight` — that prop does not exist on AgGridTable,
          so the grid silently fell back to autoHeight and collapsed to a
          sliver whenever a search returned a handful of rows. */}
      <AgGridTable columnDefs={columns} rowData={data} height="60vh" serverSide refData={() => {}} onSortChanged={handleSortChanged} />
      <div className="tw-px-4 tw-py-3 tw-border-t tw-border-slate-100 tw-bg-white">
        <Pagination
          currentPage={currentParams.page || 1} totalPages={totalPages} totalResults={totalRows}
          perPage={pageSize} onPageChange={p => goToPage(p)} onPageSizeChange={s => goToPage(1, s)}
          pageSizeOptions={[50, 100, 200]}
        />
      </div>
    </>
  );
};
ProjectTabContent.propTypes = {
  config: PropTypes.shape({ stateKey: PropTypes.string.isRequired, loadApi: PropTypes.func.isRequired, columnDefs: PropTypes.array.isRequired }).isRequired,
  entryData: PropTypes.object,
};

// ── Search box ────────────────────────────────────────────────────────────────

/**
 * The search input, isolated so a keystroke re-renders THIS component and
 * nothing else.
 *
 * It used to be controlled by state on ProjectPage itself, which meant every
 * character re-rendered the entire page — Pipeline Overview, the AG Grid with
 * fifty rows, the kanban configs, all of it. Chrome's engine absorbed that;
 * Safari did not, and typing in the box felt like the page had hung. The
 * debounce only limited the REQUESTS — the rendering cost per keystroke was
 * untouched, and that cost was the part Safari choked on.
 *
 * `committedValue` is the search actually in force (from the tab's params).
 * Local state follows it, so a tab switch, a removed tag or Clear-all resets
 * the box from outside without owning every keystroke.
 */
const ProjectSearchBox = React.memo(({ committedValue, onApply }) => {
  const [value, setValue] = useState(committedValue);
  const timer = useRef(null);

  useEffect(() => { setValue(committedValue); }, [committedValue]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const handleChange = (next) => {
    setValue(next);
    clearTimeout(timer.current);

    const term = next.trim();

    // Clearing is immediate — waiting to show everything again feels broken.
    if (!term) {
      timer.current = setTimeout(() => onApply(''), 0);
      return;
    }

    // A one- or two-character term matches nearly every project and returns
    // the least useful, most expensive page — so it is simply never sent.
    if (term.length < SEARCH_MIN_LENGTH) return;

    timer.current = setTimeout(() => onApply(term), SEARCH_DEBOUNCE_MS);
  };

  return (
    <div className="tw-relative">
      <MdSearch className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 tw-pointer-events-none" size={14} />
      <input type="text" placeholder="Search..." value={value}
        onChange={e => handleChange(e.target.value)}
        className="tw-pl-8 tw-pr-7 tw-py-1.5 tw-rounded tw-border tw-border-slate-200 tw-text-xs tw-w-44 tw-bg-white focus:tw-outline-none focus:tw-border-primary-400 focus:tw-ring-2 focus:tw-ring-primary-100/50 tw-transition-all" />
      {value && (
        <button type="button" onClick={() => handleChange('')}
          className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 hover:tw-text-slate-600 tw-cursor-pointer tw-bg-transparent tw-border-0 tw-p-0">
          <MdClose size={13} />
        </button>
      )}
      {/* Otherwise a short search looks like a broken box: the user types,
          nothing changes, and nothing says why. */}
      {value.trim().length > 0 && value.trim().length < SEARCH_MIN_LENGTH && (
        <p className="tw-absolute tw-left-0 tw-top-full tw-mt-1 tw-m-0 tw-text-[11px] tw-text-slate-400 tw-whitespace-nowrap">
          Keep typing — {SEARCH_MIN_LENGTH} characters to search
        </p>
      )}
    </div>
  );
});
ProjectSearchBox.displayName = 'ProjectSearchBox';
ProjectSearchBox.propTypes = {
  committedValue: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
};

// ── Page ───────────────────────────────────────────────────────────────────────

const ProjectPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [showFilter, setShowFilter]           = useState(false);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const statsRequest = useRef(null);

  const { permissions } = useContext(PermissionContext);
  const canCreate = permissions?.v901 === true;

  const projectState = useSelector(state => state.crm_project);
  const dispatch     = useDispatch();
  const navigate     = useNavigate();

  // Pipeline stats live in Redux so all three views (Pipeline Overview,
  // All Projects, Kanban) stay in sync when filters are applied.
  const pipelineStats = projectState.pipelineStats;

  // Derive counts from Redux pipeline stats (single source of truth)
  const statsCounts = useMemo(() => {
    const d = pipelineStats || {};
    const meta = d._meta || {};
    return {
      all:         meta.all_count       ?? 0,
      new:         d.new_pcv?.count     ?? 0,
      quotation:   d.quotation?.count   ?? 0,
      negotiation: d.negotiation?.count ?? 0,
      job:         d.job?.count         ?? 0,
      lost:        d.lost?.count        ?? 0,
    };
  }, [pipelineStats]);

  const monthlyCount = pipelineStats?._meta?.monthly_count ?? 0;

  // Initial load — reuse the persisted All-tab filter params so the Pipeline
  // Overview matches the grids below (stats were previously always unfiltered
  // on mount, diverging from a restored filter). Also reset the fetch cache so
  // re-entering the page always refreshes data once.
  useEffect(() => {
    lastFetchedParams.clear();
    dispatch(getPipelineStats(statsParams(projectState.allData?.currentParams)));
    return () => lastFetchedParams.clear();
    // mount-only: seed stats from the persisted params once; later filter changes refetch stats in their own handlers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const isKanban  = activeTab === 'kanban';
  const allData   = projectState.allData || {};

  // Filter tags shown in filter bar — for kanban use first stage's tags (they're all the same after a filter apply)
  const displayTags = useMemo(
    () => isKanban ? (projectState[STAGE_TABS[0].stateKey]?.filterTags || []) : (allData.filterTags || []),
    [isKanban, projectState, allData.filterTags]
  );

  // The search actually in force for the visible view. Derived, not state:
  // the box below follows it, so tab switches and tag removals reset the box
  // without the page owning every keystroke.
  const committedSearch = (isKanban
    ? projectState[STAGE_TABS[0].stateKey]?.currentParams
    : allData.currentParams)?.[SEARCH_ATTR] || '';

  const monthLabel = useMemo(() => {
    const d = new Date();
    return `${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`;
  }, []);

  const pipelineTotals = useMemo(() => {
    if (!pipelineStats) return { amount: null, weight: null };
    const vals   = ACTIVE_STAGE_TABS.map((t) => pipelineStats[t.statsKey]).filter(Boolean);
    const amount = vals.reduce((s, v) => s + (v?.amount || 0), 0);
    const weight = vals.reduce((s, v) => s + (v?.weight || 0), 0);
    return { amount: vals.length ? amount : null, weight: vals.length ? weight : null };
  }, [pipelineStats]);

  const totalCount = useMemo(
    () => { const s = ACTIVE_STAGE_TABS.reduce((acc, t) => acc + (statsCounts[t.key] || 0), 0); return s || null; },
    [statsCounts]
  );

  // Apply search to every view (All Projects grid + all kanban stages) so the
  // shared toolbar behaves the same regardless of the active tab
  const applySearch = useCallback((value) => {
    ALL_TABS.forEach(tab => {
      const stData = projectState[tab.stateKey] || {};
      const params = { ...stData.currentParams, page: 1, [SEARCH_ATTR]: value || undefined };
      dispatch(setNestedParams({ key: tab.stateKey, params }));
      const base = (stData.filterTags || []).filter(t => t.id !== SEARCH_ATTR);
      dispatch(setFilterTag({ key: tab.stateKey, tags: value ? [...base, { id: SEARCH_ATTR, name: 'Search', value }] : base }));
    });
    // Keep Pipeline Overview in sync with the search condition
    const allParams = projectState[ALL_CONFIG.stateKey]?.currentParams || {};
    // Cancels the previous one for the same reason as the grid above: these
    // arrive out of order, and the tile row would end up showing counts for a
    // search the box no longer contains.
    statsRequest.current?.abort();
    statsRequest.current = dispatch(
      getPipelineStats(statsParams({ ...allParams, [SEARCH_ATTR]: value || undefined }))
    );
  }, [projectState, dispatch]);


  const handleRemoveTag = useCallback((tagId) => {
    ALL_TABS.forEach(tab => {
      const stData = projectState[tab.stateKey] || {};
      const newTags   = (stData.filterTags || []).filter(t => t.id !== tagId);
      const newParams = { ...stData.currentParams };
      delete newParams[tagId];
      dispatch(setFilterTag({ key: tab.stateKey, tags: newTags }));
      dispatch(setNestedParams({ key: tab.stateKey, params: newParams }));
    });
    // Re-sync Pipeline Overview without the removed condition
    const remaining = { ...(projectState[ALL_CONFIG.stateKey]?.currentParams || {}) };
    delete remaining[tagId];
    dispatch(getPipelineStats(statsParams(remaining)));
  }, [projectState, dispatch]);

  const handleClearAll = useCallback(() => {
    ALL_TABS.forEach(tab => {
      dispatch(resetNestedParams({ key: tab.stateKey }));
      dispatch(resetFilterTag({ key: tab.stateKey }));
    });
    // Back to unfiltered totals
    dispatch(getPipelineStats());
  }, [dispatch]);

  // Filter applies to ALL three views simultaneously:
  //   1. All Projects grid
  //   2. Kanban columns (all 5 stages)
  //   3. Pipeline Overview (stats cards re-fetch with same params)
  const handleFilterApply = useCallback((params, tags) => {
    ALL_TABS.forEach(tab => {
      dispatch(setNestedParams({ key: tab.stateKey, params }));
      dispatch(setFilterTag({ key: tab.stateKey, tags }));
    });
    // Re-fetch pipeline stats with the active filter params
    dispatch(getPipelineStats(params));
    setShowFilter(false);
  }, [dispatch]);

  const handleFilterReset = useCallback(() => {
    ALL_TABS.forEach(tab => {
      dispatch(resetNestedParams({ key: tab.stateKey }));
      dispatch(resetFilterTag({ key: tab.stateKey }));
    });
    // Reset pipeline stats to unfiltered totals
    dispatch(getPipelineStats());
  }, [dispatch]);

  // Local, not the persisted slice: a reload mid-export must not leave the
  // button stuck in "Processing".
  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await dispatch(exportProjects({ ...allData.currentParams, export_status: 'all' })).unwrap();
    } catch {
      // apiAxios already surfaces the error toast.
    } finally {
      setExporting(false);
    }
  }, [dispatch, allData.currentParams, exporting]);

  const hasActiveFilter = displayTags.length > 0;

  return (
    <div className="tw-w-full tw-max-w-[1800px] tw-mx-auto tw-px-3 sm:tw-px-5 xl:tw-px-8 tw-py-3 sm:tw-py-5 tw-flex tw-flex-col tw-gap-4 tw-bg-[#f4f7fb]">

      {/* ── Header ── */}
      <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-3">
        <div>
          <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-primary-600 tw-mb-1">CRM workspace</p>
          <h1 className="tw-text-2xl tw-font-bold tw-text-slate-900 tw-mb-0">Project List</h1>
        </div>
        <div className="tw-flex tw-w-full sm:tw-w-auto tw-items-center tw-gap-2">
          {canCreate && (
            <Button variant="primary" size="sm" leftIcon={MdAdd} onClick={() => setShowCreateDrawer(true)} className="tw-flex-1 sm:tw-flex-none tw-shadow-sm">
              New Project (PCV)
            </Button>
          )}
          <Button variant="secondary" size="sm" leftIcon={MdArrowBack} onClick={() => navigate(-1)} className="tw-flex-1 sm:tw-flex-none tw-shadow-sm">
            Back
          </Button>
        </div>
      </div>

      {/* ── New Project Drawer ── */}
      <Drawer
        show={showCreateDrawer}
        onHide={() => setShowCreateDrawer(false)}
        title={<><MdAdd size={16} className="tw-text-primary-500" /> New Project (PCV)</>}
        width="70%"
        minWidth="640px"
        backdropClick={false}
      >
        <ProjectForm
          title="Create new project"
          onClose={() => setShowCreateDrawer(false)}
          onSuccess={(projectId) => {
            setShowCreateDrawer(false);
            navigate(`/crm/projects/${projectId}`);
          }}
        />
      </Drawer>

      {/* ── Sales pipeline overview ── */}
      <div className="tw-rounded-xl tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #f8faff 0%, #ffffff 100%)' }}>

        {/* Section header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-pt-4 tw-pb-0">
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-rounded tw-p-1.5" style={{ background: '#4f46e510' }}>
              <MdBarChart size={14} style={{ color: '#4f46e5' }} />
            </div>
            <span className="tw-text-sm tw-font-bold tw-text-slate-700">Pipeline Overview</span>
            {pipelineStats?._meta?.filtered && (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-[10px] tw-font-bold tw-bg-primary-50 tw-text-primary-600 tw-border tw-border-primary-100">
                <MdTune size={10} /> Filtered
              </span>
            )}
          </div>
        </div>

        {/* Summary metrics */}
        <div className="tw-grid tw-grid-cols-1 min-[460px]:tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-3 tw-p-4">
          <SummaryCard icon={MdBarChart}      label="Total Projects" value={totalCount?.toLocaleString() ?? '—'}       sub="All active stages"    accent="#64748b" />
          <SummaryCard icon={MdAttachMoney}   label="Total Amount"   value={fmtUSD(pipelineTotals.amount)}              sub="USD — pipeline value"  accent="#4f46e5" />
          <SummaryCard icon={MdFitnessCenter} label="Total Weight"   value={`${fmtMT(pipelineTotals.weight)} MT`}       sub="Estimated metric tons" accent="#0891b2" />
          <SummaryCard icon={MdCalendarMonth} label="New This Month" value={monthlyCount?.toLocaleString() ?? '—'}      sub={monthLabel}            gradient />
        </div>

        {/* Pipeline stages */}
        <div className="tw-px-4 tw-pb-4">
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3">
            <div className="tw-h-px tw-flex-1" style={{ background: 'linear-gradient(90deg, #e2e8f0, transparent)' }} />
            <span className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest tw-px-2">Sales Pipeline</span>
            <div className="tw-h-px tw-flex-1" style={{ background: 'linear-gradient(90deg, transparent, #e2e8f0)' }} />
          </div>
          <div className="table-scroll tw-grid tw-grid-flow-col auto-cols-[minmax(185px,1fr)] xl:tw-grid-flow-col xl:tw-auto-cols-fr tw-items-stretch tw-gap-2 tw-overflow-x-auto tw-pb-1">
            {STAGE_TABS.map(tab => (
              <PipelineStageCard
                key={tab.key}
                tab={tab}
                count={statsCounts[tab.key]}
                amount={pipelineStats?.[tab.statsKey]?.amount}
                weight={pipelineStats?.[tab.statsKey]?.weight}
                breakdown={pipelineStats?.[tab.statsKey]?.breakdown}
                isActive={false}
                onClick={() => setActiveTab('kanban')}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Main card ── */}
      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">

        {/* Tab bar — 2 tabs + stage indicators when on kanban */}
        <SimpleBar>
          <div className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-border-b tw-border-slate-100">

            {/* All Projects tab */}
            <button type="button" onClick={() => setActiveTab('all')}
              className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-xl tw-text-sm tw-font-medium tw-whitespace-nowrap tw-transition-all tw-duration-150 tw-border-0 tw-cursor-pointer ${
                !isKanban ? 'tw-bg-primary-600 tw-text-white tw-shadow-sm' : 'tw-bg-transparent tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-700'
              }`}>
              <MdList size={14} />
              All Projects
              {(projectState.allData?.totalRows > 0) && (
                <span className={`tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-text-[10px] tw-font-bold tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-tabular-nums ${
                  !isKanban ? 'tw-bg-white/25 tw-text-white' : 'tw-bg-slate-100 tw-text-slate-600'
                }`}>{projectState.allData.totalRows > 999 ? '999+' : projectState.allData.totalRows}</span>
              )}
            </button>

            {/* Kanban tab */}
            <button type="button" onClick={() => setActiveTab('kanban')}
              className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-xl tw-text-sm tw-font-medium tw-whitespace-nowrap tw-transition-all tw-duration-150 tw-border-0 tw-cursor-pointer ${
                isKanban ? 'tw-bg-primary-600 tw-text-white tw-shadow-sm' : 'tw-bg-transparent tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-700'
              }`}>
              <MdViewKanban size={14} />
              Kanban
            </button>

            {/* Stage pipeline indicators — visible on kanban tab */}
            {isKanban && (
              <div className="tw-flex tw-items-center tw-gap-0.5 tw-ml-2 tw-pl-2 tw-border-l tw-border-slate-100">
                {STAGE_TABS.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <div key={tab.key} className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-xl" style={{ backgroundColor: `${tab.accent}18` }}>
                      <Icon size={11} style={{ color: tab.accent }} />
                      <span className="tw-text-[11px] tw-font-semibold" style={{ color: tab.accent }}>{tab.label}</span>
                      {statsCounts[tab.key] != null && (
                        <span className="tw-text-[10px] tw-font-bold tw-tabular-nums" style={{ color: tab.accent }}>
                          {statsCounts[tab.key] > 999 ? '999+' : statsCounts[tab.key]}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Shared controls — apply to both All Projects and Kanban views */}
            <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0 tw-ml-auto tw-pl-3">
              <ProjectSearchBox committedValue={committedSearch} onApply={applySearch} />
              <button type="button" onClick={() => setShowFilter(true)}
                className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-cursor-pointer tw-transition-all tw-border ${
                  hasActiveFilter ? 'tw-bg-primary-600 tw-text-white tw-border-primary-600 hover:tw-bg-primary-700' : 'tw-text-slate-600 tw-bg-white tw-border-slate-200 hover:tw-bg-slate-50'
                }`}>
                <MdTune size={14} /> Filter
              </button>
              <Button variant="secondary" size="sm" leftIcon={MdFileDownload} onClick={handleExport}
                loading={exporting} aria-busy={exporting}
                title={exporting ? 'Exporting projects, this may take a moment…' : undefined}
                className="tw-text-emerald-700 hover:tw-bg-emerald-50 hover:tw-border-emerald-300">
                {exporting ? 'Processing…' : 'Export'}
              </Button>
            </div>
          </div>
        </SimpleBar>

        {/* Active filter tags — only rendered when filters are applied */}
        {hasActiveFilter && (
          <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-1.5 tw-px-4 tw-py-2.5 tw-border-b tw-border-slate-100 tw-bg-slate-50/70">
            <span className="tw-text-[10px] tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wide tw-whitespace-nowrap">Active ({displayTags.length})</span>
            {displayTags.map(tag => (
              <span key={tag.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-primary-50 tw-border tw-border-primary-100 tw-text-primary-700 tw-rounded-xl tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-max-w-[200px]">
                <span className="tw-font-semibold tw-shrink-0">{tag.name}:</span>
                <span className="tw-truncate">{Array.isArray(tag.value) ? tag.value.join(', ') : String(tag.value)}</span>
                <button type="button" onClick={() => handleRemoveTag(tag.id)}
                  className="tw-ml-0.5 tw-text-primary-400 hover:tw-text-primary-700 tw-cursor-pointer tw-bg-transparent tw-border-0 tw-p-0 tw-leading-none tw-shrink-0">
                  <MdClose size={12} />
                </button>
              </span>
            ))}
            <button type="button" onClick={handleClearAll}
              className="tw-text-[11px] tw-text-slate-400 hover:tw-text-red-500 tw-cursor-pointer tw-bg-transparent tw-border-0 tw-p-0 tw-underline tw-underline-offset-2 tw-transition-colors">
              Clear all
            </button>
          </div>
        )}

        {/* Content */}
        {isKanban ? (
          <div className="tw-p-3 sm:tw-p-4 tw-bg-slate-50 tw-border-t tw-border-slate-200">
            <KanbanView projectState={projectState} pipelineStats={pipelineStats} />
          </div>
        ) : (
          <div className="tw-bg-slate-50 tw-min-h-[400px] tw-p-3 sm:tw-p-4 tw-border-t tw-border-slate-200">
            <p className="tw-text-xs tw-text-slate-400 tw-mb-3">All projects across every status.</p>
            <ProjectTabContent key="all" config={ALL_CONFIG} entryData={allData} />
          </div>
        )}
      </div>

      {/* Filter modal */}
      {showFilter && (
        <FilterAdvanceModal
          showModal={showFilter}
          setshowModal={setShowFilter}
          data={allData}
          entryKey={ALL_CONFIG.stateKey}
          onApply={handleFilterApply}
          onReset={handleFilterReset}
        />
      )}
    </div>
  );
};

export default ProjectPage;
